import { useEffect, useState } from 'react';
import { useI18n } from '../../context/useI18n';
import { useAuth } from '../../context/useAuth';
import {
  isCoachClientPendingActivation,
  isDeferredCycleEnrollment,
  isSedeVigenciaConsumer,
  pendingPaymentMessage,
} from '../../utils/subscriptionAccess';
import { isFoodOnlyCommercialUser } from './userServices';
import { buildSupportWaUrl } from '../../utils/whatsappSupport';
import { isFutbolOnlyConsumer } from '../../utils/futbolModule';
import { mergeCoachBrand } from '../../utils/branding';
import api from '../../services/api';

export default function PendingPaymentBanner({ onOpenAccount }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const coachClientPending = isCoachClientPendingActivation(user);
  const sedePending = !coachClientPending && isSedeVigenciaConsumer(user);
  const deferredCycle = !sedePending && !coachClientPending && isDeferredCycleEnrollment(user);
  const foodOnlyPending = !coachClientPending && !sedePending && !deferredCycle
    && isFoodOnlyCommercialUser(user);
  const coachBrand = mergeCoachBrand(user, user?.coach_branding);
  const brandName = coachBrand?.brand_name || coachBrand?.nombre || user?.coach_branding?.brand_name || 'tu coach';
  const sedeFirst = !coachClientPending && Boolean(user?.gym_parent_id || user?.is_sede_final);
  const [waUrl, setWaUrl] = useState(() => buildSupportWaUrl({
    gym: coachClientPending || foodOnlyPending || isFutbolOnlyConsumer(user)
      ? null
      : (user?.gym || (user?.gym_id != null ? { id: user.gym_id, parent_gym_id: user.gym_parent_id } : null)),
    planSupport: user?.plan_support || null,
    trainer: isFutbolOnlyConsumer(user) ? null : (user?.trainer || user?.coach_branding || null),
    sedeFirst,
    user,
  }));

  // Misma fuente que Mi Cuenta → /communications/support (gym sede / coach / plan).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/communications/support');
        const url = res.data?.data?.wa_url || null;
        if (!cancelled && url) setWaUrl(url);
      } catch {
        /* keep fallback */
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id, user?.gym_id, user?.trainer_id]);

  return (
    <div
      className={`rounded-xl border p-4 sm:p-5 space-y-3 ${
        deferredCycle
          ? 'border-[var(--d28d-primary,#0d9488)]/35 bg-[var(--d28d-primary,#0d9488)]/8'
          : foodOnlyPending
            ? 'border-emerald-500/40 bg-emerald-500/10'
            : 'border-amber-400/40 bg-amber-500/10'
      }`}
      role="alert"
    >
      <h2 className="font-bold text-[var(--d28d-text)] text-sm uppercase tracking-wide">
        {coachClientPending
          ? t('account.pending_coach_title', 'Bienvenido/a a {brand}', { brand: brandName })
          : deferredCycle
            ? t('account.deferred_welcome_title', 'Bienvenida a Fit Live')
            : sedePending
              ? t('account.pending_sede_title', 'Bienvenido/a a las clases virtuales de Fit Live')
              : foodOnlyPending
                ? t('account.pending_food_title', 'FOOD PLAN — pendiente de activación')
                : t('account.pending_title', 'Servicio pendiente de activación')}
      </h2>
      <p className="text-sm text-[var(--d28d-text)] opacity-90">
        {pendingPaymentMessage(t, user)}
      </p>
      {!deferredCycle ? (
        <p className="text-xs d28d-text-muted">
          {coachClientPending
            ? t(
              'account.pending_coach_hint',
              'Mientras tanto solo puedes usar Mi Cuenta (para contratar FOOD PLAN con Wompi) y Soporte. El entrenamiento, biblioteca y progreso se habilitan cuando tu coach active tu acceso.',
            )
            : sedePending
              ? t(
                'account.pending_sede_hint',
                'Mientras tanto solo puedes revisar Mi Cuenta y Soporte. El administrador de tu sede activará tu perfil según los días de vigencia. Cualquier duda, contacta a soporte.',
              )
              : foodOnlyPending
                ? t(
                  'account.pending_food_hint',
                  'Mientras validamos tu pago o un administrador activa FOOD PLAN, solo puedes usar Mi Cuenta y Soporte. Al activarse entrarás directo a tu plan de alimentación.',
                )
                : t(
                  'account.pending_hint',
                  'Mientras tanto solo puedes revisar Mi Cuenta y Soporte. Cuando un administrador active tu acceso, se habilitarán clases, progreso y demás funciones.',
                )}
        </p>
      ) : null}
      <div className="flex flex-wrap gap-2">
        {waUrl ? (
          <a
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-primary text-sm"
          >
            {t('support.whatsapp', 'WhatsApp soporte')}
          </a>
        ) : null}
        {onOpenAccount ? (
          <button type="button" className="btn btn-secondary text-sm" onClick={onOpenAccount}>
            {t('nav.myaccount', 'Mi Cuenta')}
          </button>
        ) : null}
      </div>
    </div>
  );
}
